import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Fire-and-forget log to push_debug_logs table */
async function dbLog(userId: string | undefined, step: string, message: string) {
  logger.log(`[Push][${step}] ${message}`);
  if (!userId) return;
  try {
    await supabase.from('push_debug_logs' as any).insert({
      user_id: userId,
      step,
      message: message.substring(0, 500),
    } as any);
  } catch {
    // never block push flow for logging
  }
}

/**
 * Wait for a service worker registration to become active.
 */
function waitForActive(
  reg: ServiceWorkerRegistration,
  timeoutMs: number
): Promise<ServiceWorkerRegistration | null> {
  return new Promise((resolve) => {
    if (reg.active) { resolve(reg); return; }

    const timer = setTimeout(() => resolve(null), timeoutMs);
    const sw = reg.installing || reg.waiting;
    if (!sw) { clearTimeout(timer); resolve(null); return; }

    const onStateChange = () => {
      if (sw.state === 'activated') {
        clearTimeout(timer);
        sw.removeEventListener('statechange', onStateChange);
        resolve(reg);
      } else if (sw.state === 'redundant') {
        clearTimeout(timer);
        sw.removeEventListener('statechange', onStateChange);
        resolve(null);
      }
    };
    sw.addEventListener('statechange', onStateChange);
  });
}

/** Detect if an error is recoverable (worth retrying) */
function isRecoverableError(err: any): boolean {
  const msg = (err?.message || '').toLowerCase();
  const name = (err?.name || '').toLowerCase();
  // Permission denied is permanent
  if (name === 'notallowederror' || msg.includes('permission denied')) return false;
  // Everything else (timeout, abort, network) is recoverable
  return true;
}

/** Session-scoped retry counter to avoid infinite loops */
const SESSION_MAX_RETRIES = 4;

// Reset session counter on new browser sessions
const SESSION_KEY = 'push-session-id';
const currentSession = Date.now().toString(36);
if (typeof sessionStorage !== 'undefined') {
  const prevSession = sessionStorage.getItem(SESSION_KEY);
  if (prevSession !== currentSession) {
    sessionStorage.setItem(SESSION_KEY, currentSession);
  }
}

let sessionRetryCount = 0;

export function usePushSubscription() {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const autoRetryDone = useRef(false);
  const subscribeRef = useRef<(() => Promise<boolean>) | null>(null);

  // Keep ref in sync so useEffect can call subscribe without circular deps
  useEffect(() => {
    subscribeRef.current = subscribeImpl;
  });

  // Reset session retry counter on new sessions (tab open)
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') {
      const key = 'push-retry-reset-done';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        sessionRetryCount = 0;
      }
    }
  }, []);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported && user) {
      const uid = user.id;
      navigator.serviceWorker.getRegistration('/').then(async (reg) => {
        if (reg?.active) {
          const sub = await reg.pushManager.getSubscription();
          setIsSubscribed(!!sub);
          
          if (!sub && 'Notification' in window && window.Notification.permission === 'granted') {
            const status = localStorage.getItem('sound-permission-status');
            if (status === 'push-registered' || status === 'accepted' || status === 'push-timeout' || status === 'retry-needed') {
              // Subscription lost — auto-resubscribe directly
              if (!autoRetryDone.current) {
                autoRetryDone.current = true;
                // Reset session counter for lost-subscription recovery
                sessionRetryCount = 0;
                dbLog(uid, 'auto_resub', `Subscription lost (status: ${status}), auto-resubscribing...`);
                subscribeRef.current?.().then(ok => {
                  if (ok) {
                    localStorage.setItem('sound-permission-status', 'push-registered');
                    setIsSubscribed(true);
                  }
                });
              }
            }
          }
        }
      }).catch(() => {});
    }
  }, [user]);

  const subscribeImpl = useCallback(async (): Promise<boolean> => {
    if (!user || !isSupported) return false;
    
    // Session retry limit to prevent loops
    if (sessionRetryCount >= SESSION_MAX_RETRIES) {
      logger.log('[Push] Session retry limit reached, skipping');
      return false;
    }
    
    const uid = user.id;
    const attemptId = Date.now().toString(36);
    sessionRetryCount++;

    try {
      // ── STEP 0: Explicit Notification permission request ──
      // Some browsers (notably Safari desktop and certain Chrome builds)
      // do NOT show the native permission prompt as a side-effect of
      // pushManager.subscribe(). Calling Notification.requestPermission()
      // explicitly — synchronously inside the click-driven call stack —
      // guarantees the user sees the browser dialog.
      if ('Notification' in window) {
        let perm = window.Notification.permission;
        if (perm === 'default') {
          await dbLog(uid, 'step0_perm', `[${attemptId}] Requesting Notification permission...`);
          try {
            perm = await window.Notification.requestPermission();
          } catch (e: any) {
            await dbLog(uid, 'step0_perm_err', `[${attemptId}] ${e?.name}: ${e?.message}`);
          }
        }
        if (perm === 'denied') {
          await dbLog(uid, 'step0_denied', `[${attemptId}] Permission denied`);
          return false;
        }
        if (perm !== 'granted') {
          await dbLog(uid, 'step0_dismissed', `[${attemptId}] Permission still '${perm}' after prompt`);
          return false;
        }
      }

      // ── STEP 1: Get active SW ──
      await dbLog(uid, 'step1_sw', `[${attemptId}] Getting push-sw.js registration...`);
      
      let reg = await navigator.serviceWorker.getRegistration('/');

      // Prefer reusing the registration owned by main.tsx. Only register
      // ourselves as a last resort — registering twice causes competing
      // lifecycles and inconsistent SW state across users.
      if (!reg) {
        const all = await navigator.serviceWorker.getRegistrations();
        reg = all.find((r) =>
          (r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '').includes('push-sw')
        );
      }
      if (!reg || !(reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '').includes('push-sw')) {
        await dbLog(uid, 'step1_register', `[${attemptId}] No existing push-sw registration, registering fresh...`);
        reg = await navigator.serviceWorker.register('/push-sw.js', { updateViaCache: 'none' });
      } else {
        await dbLog(uid, 'step1_reuse', `[${attemptId}] Reusing existing push-sw registration`);
      }

      if (!reg.active) {
        const activated = await waitForActive(reg, 10000);
        if (!activated) {
          await dbLog(uid, 'step1_ready', `[${attemptId}] Falling back to .ready...`);
          reg = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<never>((_, rej) => setTimeout(() => rej(new Error('ready timeout')), 10000)),
          ]);
        } else {
          reg = activated;
        }
      }

      await dbLog(uid, 'step1_done', `[${attemptId}] SW active: ${reg.active?.scriptURL}`);

      // ── STEP 2: Fetch VAPID key ──
      await dbLog(uid, 'step2_vapid', `[${attemptId}] Fetching VAPID...`);
      const vapidUrl = `${import.meta.env.VITE_CEREBRO_SUPABASE_URL}/functions/v1/send-push-notification`;

      const vapidRes = await Promise.race([
        fetch(vapidUrl, {
          headers: { apikey: import.meta.env.VITE_CEREBRO_SUPABASE_PUBLISHABLE_KEY },
        }),
        new Promise<never>((_, rej) => setTimeout(() => rej(new Error('VAPID fetch timeout')), 15000)),
      ]);

      if (!vapidRes.ok) {
        const body = await vapidRes.text();
        await dbLog(uid, 'step2_fail', `[${attemptId}] HTTP ${vapidRes.status}: ${body.substring(0, 200)}`);
        return false;
      }

      const { publicKey } = await vapidRes.json();
      if (!publicKey) {
        await dbLog(uid, 'step2_fail', `[${attemptId}] Empty VAPID key`);
        return false;
      }
      await dbLog(uid, 'step2_done', `[${attemptId}] Key: ${publicKey.substring(0, 20)}...`);

      // ── STEP 3: Get or create push subscription ──
      await dbLog(uid, 'step3_sub', `[${attemptId}] Getting/creating subscription...`);

      let subscription: PushSubscription | null = null;

      // Try to get existing first (with timeout)
      try {
        subscription = await Promise.race([
          reg.pushManager.getSubscription(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
        ]);
      } catch (e: any) {
        await dbLog(uid, 'step3_get_err', `[${attemptId}] ${e?.name}: ${e?.message}`);
      }

      if (subscription) {
        await dbLog(uid, 'step3_existing', `[${attemptId}] Reusing: ${subscription.endpoint.substring(0, 60)}...`);
      } else {
        // Create new subscription — 20s timeout for slow mobile connections
        await dbLog(uid, 'step3_create', `[${attemptId}] Creating new subscription...`);
        
        const maxSubAttempts = 2;
        for (let subAttempt = 1; subAttempt <= maxSubAttempts; subAttempt++) {
          try {
            const applicationServerKey = urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer;
            subscription = await Promise.race([
              reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey,
              }),
              new Promise<never>((_, rej) => setTimeout(() => rej(new Error('subscribe timeout 20s')), 20000)),
            ]);
            await dbLog(uid, 'step3_created', `[${attemptId}] OK on attempt ${subAttempt}: ${subscription.endpoint.substring(0, 60)}...`);
            // Reset retry counter on success
            sessionRetryCount = 0;
            break;
          } catch (subErr: any) {
            const recoverable = isRecoverableError(subErr);
            await dbLog(uid, 'step3_fail', `[${attemptId}] Attempt ${subAttempt}/${maxSubAttempts} - ${subErr?.name}: ${subErr?.message} (recoverable: ${recoverable})`);
            
            if (!recoverable) {
              // Permanent failure (e.g. permission denied)
              return false;
            }
            
            if (subAttempt < maxSubAttempts) {
              // Backoff before retry: 3s for AbortError, 2s otherwise
              const backoff = subErr?.name === 'AbortError' ? 3000 : 2000;
              await dbLog(uid, 'step3_backoff', `[${attemptId}] Waiting ${backoff}ms before retry...`);
              await new Promise(r => setTimeout(r, backoff));
            } else {
              // All sub-attempts exhausted
              if (sessionRetryCount >= SESSION_MAX_RETRIES) {
                localStorage.setItem('sound-permission-status', 'push-timeout');
                await dbLog(uid, 'step3_timeout_limit', `[${attemptId}] Timeout limit reached, marked push-timeout`);
              }
              return false;
            }
          }
        }
      }

      if (!subscription) {
        await dbLog(uid, 'step3_null', `[${attemptId}] subscription is null`);
        return false;
      }

      // ── STEP 4: Validate subscription data ──
      const subJson = subscription.toJSON();
      if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
        await dbLog(uid, 'step4_invalid', `[${attemptId}] Missing fields: endpoint=${!!subJson.endpoint}, p256dh=${!!subJson.keys?.p256dh}, auth=${!!subJson.keys?.auth}`);
        return false;
      }

      // ── STEP 5: Save to database ──
      await dbLog(uid, 'step5_save', `[${attemptId}] Saving to DB...`);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        await dbLog(uid, 'step5_no_session', `[${attemptId}] No auth session`);
        return false;
      }

      // Delete all old subscriptions for this user before inserting the new one
      // This prevents duplicate push notifications from stale endpoints
      await dbLog(uid, 'step5_cleanup', `[${attemptId}] Removing old subscriptions...`);
      const { error: deleteError } = await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', uid)
        .neq('endpoint', subJson.endpoint!);

      if (deleteError) {
        await dbLog(uid, 'step5_cleanup_warn', `[${attemptId}] Cleanup warning (non-blocking): ${deleteError.message}`);
      }

      const { error: upsertError } = await supabase
        .from('push_subscriptions')
        .upsert(
          {
            user_id: uid,
            endpoint: subJson.endpoint!,
            keys_p256dh: subJson.keys!.p256dh!,
            keys_auth: subJson.keys!.auth!,
          },
          { onConflict: 'user_id,endpoint' }
        );

      if (upsertError) {
        await dbLog(uid, 'step5_error', `[${attemptId}] ${upsertError.code}: ${upsertError.message}`);
        // Retry once
        await new Promise(r => setTimeout(r, 1000));
        const { error: retryError } = await supabase
          .from('push_subscriptions')
          .upsert(
            {
              user_id: uid,
              endpoint: subJson.endpoint!,
              keys_p256dh: subJson.keys!.p256dh!,
              keys_auth: subJson.keys!.auth!,
            },
            { onConflict: 'user_id,endpoint' }
          );
        if (retryError) {
          await dbLog(uid, 'step5_retry_fail', `[${attemptId}] ${retryError.code}: ${retryError.message}`);
          return false;
        }
      }

      // ── STEP 6: Verify it was actually saved ──
      await dbLog(uid, 'step6_verify', `[${attemptId}] Verifying...`);
      const { data: verifyData, error: verifyError } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('user_id', uid)
        .eq('endpoint', subJson.endpoint!)
        .maybeSingle();

      if (verifyError || !verifyData) {
        await dbLog(uid, 'step6_fail', `[${attemptId}] Verify failed: ${verifyError?.message || 'no row found'}`);
        return false;
      }

      await dbLog(uid, 'step6_done', `[${attemptId}] ✅ Subscription saved and verified! id=${verifyData.id}`);
      setIsSubscribed(true);
      return true;

    } catch (err: any) {
      await dbLog(uid, 'subscribe_error', `[${attemptId}] ${err?.name}: ${err?.message}`);
      return false;
    }
  }, [user, isSupported]);

  const subscribe = subscribeImpl;
  return { subscribe, isSubscribed, isSupported };
}
